import {
  mainnet,
  avalanche,
  avalancheFuji,
  polygonMumbai,
  arbitrum,
  sepolia,
  optimismSepolia,
  optimism,
  polygon,
  base,
  bsc,
} from 'viem/chains'
import { UnsupportedNetworkError } from 'utils/errors'
import type { Address } from 'viem'

const GRAPHQL_MAINNET_ENDPOINT =
  'https://gateway.thegraph.com/api/0f855c06c9a16092ee23a96d3b58a287/deployments/id/QmTyCFxA6XqBhFSht2H4pgo8Y5TPimuKjkJPuHiVCsQYij'

export const zeroExWrapperUrl = 'https://api.phuture.finance'

export enum NetworkType {
  Mainnet = mainnet.id,
  Avalanche = avalanche.id,
  Arbitrum = arbitrum.id,
  Optimism = optimism.id,
  Polygon = polygon.id,
  Base = base.id,
  Bsc = bsc.id,
  //TESTNETS
  AvalancheFuji = avalancheFuji.id,
  PolygonMumbai = polygonMumbai.id, //80001
  SepoliaOptimism = optimismSepolia.id, //11155420
  Sepolia = sepolia.id, //11155111
}

export const getSupportedNetworkTypeFromChainId = (chainId: number) => {
  const networkType = getNetworkTypeIfExists(chainId)
  if (!networkType) throw new UnsupportedNetworkError(chainId)

  return networkType
}

export const getNetworkTypeIfExists = (chainId: number) => {
  switch (chainId) {
    case mainnet.id:
      return NetworkType.Mainnet
    case avalanche.id:
      return NetworkType.Avalanche
    case arbitrum.id:
      return NetworkType.Arbitrum
    case optimism.id:
      return NetworkType.Optimism
    case base.id:
      return NetworkType.Base
    case bsc.id:
      return NetworkType.Bsc
    //TESTNETS
    case avalancheFuji.id:
      return NetworkType.AvalancheFuji
    case polygonMumbai.id:
      return NetworkType.PolygonMumbai
    case sepolia.id:
      return NetworkType.Sepolia
    case optimismSepolia.id:
      return NetworkType.SepoliaOptimism
    default:
      return undefined
  }
}

export const getDeprecatedNetworkType = (chainId: NetworkType): NetworkType =>
  chainId || NetworkType.Mainnet

interface ContractInterface {
  Base: Address
  NativeToken: Address
  PHTR: Address
  ePHTR: Address
  PHTRRouter: Address
  WETH: Address
  WAVAX: Address
  LPToken: Address
  USDC: Address
  IndexToken: Address
  IndexDecimals: number
}
export interface SetInterface extends ContractInterface {
  subgraph: string
  subgraphNew: string
  blockExplorer: string
  sushiPairUrl: string
}
const _contracts: { [key in number]: ContractInterface } = {
  [NetworkType.Mainnet]: {
    Base: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    NativeToken: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2', //ETH WETH
    PHTR: '0xe1fc4455f62a6e89476f1072530c20cf1a0622da',
    ePHTR: '0x3b9805E163b3750e7f13a26B06F030f2d3b799F5',
    PHTRRouter: '0x57136542BdC53aaf53bF519a6D28A6C8F7CEEB1c',
    LPToken: '0x69e92e881aa0e90218c45622d50f26073782e0c9',
    USDC: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    WETH: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
    WAVAX: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
    IndexToken: '0x632806bf5c8f062932dd121244c9fbe7becb8b48',
    IndexDecimals: 18,
  },
  [NetworkType.Avalanche]: {
    Base: '0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e',
    NativeToken: '0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7', //AVAX WAVAX
    PHTR: '0xe1fc4455f62a6e89476f1072530c20cf1a0622da',
    ePHTR: '0x3b9805E163b3750e7f13a26B06F030f2d3b799F5',
    PHTRRouter: '0x57136542BdC53aaf53bF519a6D28A6C8F7CEEB1c',
    LPToken: '0x69e92e881aa0e90218c45622d50f26073782e0c9',
    USDC: '0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e',
    WETH: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
    WAVAX: '0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7',
    IndexToken: '0x48f88a3fe843ccb0b5003e70b4192c1d7448bef0',
    IndexDecimals: 18,
  },
}

/**
 * Leverage the network type by using the chainId
 * @param chainId
 * @returns
 */
export const getNetworkInterface = (chainId: number) => {
  return sets[chainId]
}

export const sets: { [key in NetworkType]: SetInterface } = {
  [NetworkType.Mainnet]: {
    ..._contracts[NetworkType.Mainnet],
    subgraph: GRAPHQL_MAINNET_ENDPOINT,
    subgraphNew: 'https://api.studio.thegraph.com/query/39480/subgraph-test/version/latest',
    blockExplorer: 'https://etherscan.io',
    sushiPairUrl:
      'https://app.sushi.com/add/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/0xE1Fc4455f62a6E89476f1072530C20CF1A0622dA',
  },
  [NetworkType.Arbitrum]: {
    ..._contracts[NetworkType.Arbitrum],
    subgraph: GRAPHQL_MAINNET_ENDPOINT,
    // subgraphNew: 'https://api.thegraph.com/subgraphs/name/olivermehr/subgraph-test',
    subgraphNew: 'https://api.studio.thegraph.com/query/39480/subgraph-test/version/latest',
    blockExplorer: 'https://arbiscan.io',
    sushiPairUrl:
      'https://app.sushi.com/add/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/0xE1Fc4455f62a6E89476f1072530C20CF1A0622dA',
  },
  [NetworkType.Avalanche]: {
    ..._contracts[NetworkType.Avalanche],
    subgraph: 'https://api.thegraph.com/subgraphs/name/phuture-finance/phuture-avax-core',
    subgraphNew:
      'https://api.studio.thegraph.com/query/39480/phuture-subgraph-avalanche/version/latest ',
    blockExplorer: 'https://snowscan.xyz',
    sushiPairUrl:
      'https://app.sushi.com/add/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/0xE1Fc4455f62a6E89476f1072530C20CF1A0622dA',
  },
  [NetworkType.Base]: {
    ..._contracts[NetworkType.Base],
    subgraph: 'https://api.thegraph.com/subgraphs/name/phuture-finance/phuture-avax-core',
    subgraphNew:
      'https://api.studio.thegraph.com/query/39480/phuture-subgraph-avalanche/version/latest ',
    blockExplorer: 'https://basescan.org',
    sushiPairUrl:
      'https://app.sushi.com/add/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/0xE1Fc4455f62a6E89476f1072530C20CF1A0622dA',
  },
  [NetworkType.AvalancheFuji]: {
    ..._contracts[NetworkType.AvalancheFuji],
    subgraph: 'https://api.thegraph.com/subgraphs/name/phuture-finance/phuture-avax-core',
    subgraphNew:
      'https://api.studio.thegraph.com/query/39480/phuture-subgraph-avalanche/version/latest ',
    blockExplorer: 'https://testnet.snowtrace.io',
    sushiPairUrl:
      'https://app.sushi.com/add/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/0xE1Fc4455f62a6E89476f1072530C20CF1A0622dA',
  },
  [NetworkType.PolygonMumbai]: {
    ..._contracts[NetworkType.PolygonMumbai],
    subgraph: GRAPHQL_MAINNET_ENDPOINT,
    subgraphNew: 'https://api.studio.thegraph.com/query/39480/subgraph-test/version/latest',
    blockExplorer: 'https://mumbai.polygonscan.com/',
    sushiPairUrl:
      'https://app.sushi.com/add/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/0xE1Fc4455f62a6E89476f1072530C20CF1A0622dA',
  },
  [NetworkType.Sepolia]: {
    ..._contracts[NetworkType.Sepolia],
    subgraph: GRAPHQL_MAINNET_ENDPOINT,
    subgraphNew: 'https://api.studio.thegraph.com/query/39480/subgraph-test/version/latest',
    blockExplorer: 'https://sepolia.etherscan.io',
    sushiPairUrl:
      'https://app.sushi.com/add/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/0xE1Fc4455f62a6E89476f1072530C20CF1A0622dA',
  },
  [NetworkType.SepoliaOptimism]: {
    ..._contracts[NetworkType.SepoliaOptimism],
    subgraph: GRAPHQL_MAINNET_ENDPOINT,
    subgraphNew: 'https://api.studio.thegraph.com/query/39480/subgraph-test/version/latest',
    blockExplorer: 'https://sepolia-optimism.etherscan.io/',
    sushiPairUrl:
      'https://app.sushi.com/add/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/0xE1Fc4455f62a6E89476f1072530C20CF1A0622dA',
  },
}
